import { useMemo, useReducer } from 'react';

import type { TFilter } from 'types/product-list';
import { getDomainValue } from 'utils/domain';
import { createFilterArray, DEFAULT_SORT, type TSort } from 'utils/filters';

type useFiltersProps = {
  all: {
    collections: TFilter[];
    typologies: TFilter[];
    concepts: TFilter[];
    sizes: TFilter[];
    colors: TFilter[];
    prices: TFilter[];
    sustainabilities: TFilter[];
    performances: TFilter[];
  };
  available: {
    collections: TFilter[];
    typologies: TFilter[];
    concepts: TFilter[];
    sizes: TFilter[];
    colors: TFilter[];
    prices: TFilter[];
    sustainabilities: TFilter[];
    performances: TFilter[];
  };
  initial: {
    collections?: TFilter[];
    typologies?: TFilter[];
    concepts?: TFilter[];
    sizes?: TFilter[];
    colors?: TFilter[];
    prices?: TFilter[];
    sustainabilities?: TFilter[];
    performances?: TFilter[];
  };
};

function createFilterMap(all: useFiltersProps['all'] | useFiltersProps['available'] | useFiltersProps['initial']) {
  return Object.fromEntries(
    Object.entries(all).map(([key, value]) => {
      return [key, new Map(value.map((filter) => [filter.key, filter]))];
    }),
  );
}

export type FilterState = {
  open: boolean;
  sort: TSort;
  all: ReturnType<typeof createFilterMap>;
  available: ReturnType<typeof createFilterMap>;
  active: ReturnType<typeof createFilterMap>;
};

type Action =
  | {
      type: 'update_available';
      new: useFiltersProps['available'];
    }
  | {
      type: 'update_active';
      new: FilterState['active'];
    }
  | { type: 'clear_all'; default?: useFiltersProps['initial'] }
  | { type: 'set_sort'; sort: TSort }
  | { type: 'toggle_open'; open?: boolean };

function reducer(state: FilterState, action: Action) {
  switch (action.type) {
    case 'update_available':
      return {
        ...state,
        available: createFilterMap(action.new),
      };
    case 'update_active':
      return {
        ...state,
        active: action.new,
      };
    case 'clear_all':
      return {
        ...state,
        active: createFilterMap(action.default ?? {}),
      };
    case 'set_sort':
      return {
        ...state,
        sort: action.sort,
      };
    case 'toggle_open':
      return {
        ...state,
        open: action.open ?? !state.open,
      };
    default:
      return state;
  }
}

export function useFilters({ all, available, initial }: useFiltersProps) {
  const [state, dispatch] = useReducer(reducer, {
    open: false,
    sort: DEFAULT_SORT,
    all: createFilterMap(all),
    available: createFilterMap(available),
    active: createFilterMap(initial),
  });

  const filters = useMemo(() => createFilterArray(all), [all]);
  const filtersCount: number = Object.entries(state.active).reduce((acc: number, [key, values]) => {
    // en camperlab no se muestran las colecciones como activas porque no se pueden deseleccionar
    if (getDomainValue({ camperlab: true, camper: false, nnormal: false }) && key === 'collections') {
      return acc;
    }
    const mapSize = values.size as unknown as number;
    return acc + mapSize;
  }, 0);
  return {
    filters,
    state,
    dispatch,
    filtersCount,
  };
}

export function normalizeActiveFilters(active: FilterState['active']) {
  return Object.keys(active).reduce(
    (acc, key) => {
      acc[key] = Array.from(active[key].values());
      return acc;
    },
    {} as Record<string, TFilter[]>,
  );
}
